import { Injectable, inject } from "@angular/core"
import {
  Firestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "@angular/fire/firestore"
import { BehaviorSubject, Observable, Subject } from "rxjs"
import { debounceTime } from "rxjs/operators"
import {
  DashboardPreferences,
  DEFAULT_DASHBOARD_PREFERENCES,
  OwnerEditedFilterType,
  VALID_OWNER_EDITED_FILTERS,
} from "../models/user-preferences.model"

/**
 * Service for managing user preferences in Firestore
 */
@Injectable({
  providedIn: "root",
})
export class UserPreferencesService {
  private firestore = inject(Firestore)

  // Cache for dashboard preferences
  private dashboardPreferencesCache = new Map<
    string,
    BehaviorSubject<DashboardPreferences>
  >()

  // Subject for debouncing save operations
  private saveDashboardPreferencesSubject = new Subject<{
    userId: string
    preferences: Partial<DashboardPreferences>
  }>()

  constructor() {
    // Set up debounced save operations (500ms delay)
    this.saveDashboardPreferencesSubject
      .pipe(debounceTime(500))
      .subscribe(({ userId, preferences }) => {
        this.saveDashboardPreferencesInternal(userId, preferences)
      })
  }

  /**
   * Gets the dashboard preferences for a user
   * Returns an observable that emits the current preferences and caches them
   * @param userId The user's UID
   * @returns Observable of dashboard preferences
   */
  getDashboardPreferences(userId: string): Observable<DashboardPreferences> {
    // Check if we have cached preferences
    if (!this.dashboardPreferencesCache.has(userId)) {
      // Create a new BehaviorSubject with defaults
      const subject = new BehaviorSubject<DashboardPreferences>({
        ...DEFAULT_DASHBOARD_PREFERENCES,
      })
      this.dashboardPreferencesCache.set(userId, subject)

      // Load preferences from Firestore
      this.loadDashboardPreferences(userId)
    }

    return this.dashboardPreferencesCache.get(userId)!.asObservable()
  }

  /**
   * Loads dashboard preferences from Firestore
   * @param userId The user's UID
   */
  private async loadDashboardPreferences(userId: string): Promise<void> {
    try {
      const preferencesRef = doc(
        this.firestore,
        `users/${userId}/preferences/dashboard`
      )
      const preferencesSnap = await getDoc(preferencesRef)

      if (preferencesSnap.exists()) {
        const data = preferencesSnap.data() as DashboardPreferences
        // Validate and sanitize the data
        const validatedPreferences = this.validateDashboardPreferences(data)
        this.dashboardPreferencesCache.get(userId)?.next(validatedPreferences)
      } else {
        // No preferences exist, create default preferences
        await this.createDefaultDashboardPreferences(userId)
      }
    } catch (error) {
      console.error("Error loading dashboard preferences:", error)
      // Keep using the default preferences already in the cache
    }
  }

  /**
   * Creates default dashboard preferences for a new user
   * @param userId The user's UID
   */
  private async createDefaultDashboardPreferences(
    userId: string
  ): Promise<void> {
    try {
      const preferencesRef = doc(
        this.firestore,
        `users/${userId}/preferences/dashboard`
      )
      const defaultPreferences: DashboardPreferences = {
        ...DEFAULT_DASHBOARD_PREFERENCES,
        createdAt: serverTimestamp() as unknown as Timestamp,
        updatedAt: serverTimestamp() as unknown as Timestamp,
      }

      await setDoc(preferencesRef, defaultPreferences)
      this.dashboardPreferencesCache.get(userId)?.next(defaultPreferences)
    } catch (error) {
      console.error("Error creating default dashboard preferences:", error)
    }
  }

  /**
   * Validates and sanitizes dashboard preferences
   * @param data Raw data from Firestore
   * @returns Validated preferences
   */
  private validateDashboardPreferences(
    data: Partial<DashboardPreferences>
  ): DashboardPreferences {
    const validStatuses = [
      "Processing",
      "In Review",
      "Ready",
      "Exported",
      "Recording",
      "Transcribing",
      "Completed",
      "Error",
      "Canceled",
      "No Audio",
      "No Transcript",
    ]

    // Validate statusFilters - empty array is valid (means "show all")
    let statusFilters = DEFAULT_DASHBOARD_PREFERENCES.statusFilters
    if (Array.isArray(data.statusFilters)) {
      statusFilters = data.statusFilters.filter((status) =>
        validStatuses.includes(status)
      )
      // Empty array is allowed - it means "no filtering" (show all statuses)
    }

    // Validate chimeEnabled
    const chimeEnabled =
      typeof data.chimeEnabled === "boolean"
        ? data.chimeEnabled
        : DEFAULT_DASHBOARD_PREFERENCES.chimeEnabled

    // Validate summaryTypeFilter
    const summaryTypeFilter =
      typeof data.summaryTypeFilter === "string"
        ? data.summaryTypeFilter
        : DEFAULT_DASHBOARD_PREFERENCES.summaryTypeFilter

    // Validate ownerEditedFilter
    const ownerEditedFilter = VALID_OWNER_EDITED_FILTERS.includes(
      data.ownerEditedFilter as OwnerEditedFilterType
    )
      ? (data.ownerEditedFilter as OwnerEditedFilterType)
      : DEFAULT_DASHBOARD_PREFERENCES.ownerEditedFilter

    // Validate showDeleted (CAB-597). Preference docs written before this field
    // existed simply lack it, so anything non-boolean falls back to the default.
    const showDeleted =
      typeof data.showDeleted === "boolean"
        ? data.showDeleted
        : DEFAULT_DASHBOARD_PREFERENCES.showDeleted

    return {
      statusFilters,
      chimeEnabled,
      summaryTypeFilter,
      ownerEditedFilter,
      showDeleted,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    }
  }

  /**
   * Saves dashboard preferences to Firestore (debounced)
   * @param userId The user's UID
   * @param preferences Partial preferences to update
   */
  saveDashboardPreferences(
    userId: string,
    preferences: Partial<DashboardPreferences>
  ): void {
    // Optimistically update the cache
    const currentPreferences = this.dashboardPreferencesCache.get(userId)
    if (currentPreferences) {
      currentPreferences.next({
        ...currentPreferences.value,
        ...preferences,
      })
    }

    // Queue the save operation (will be debounced)
    this.saveDashboardPreferencesSubject.next({ userId, preferences })
  }

  /**
   * Internal method to save preferences to Firestore
   * @param userId The user's UID
   * @param preferences Partial preferences to update
   */
  private async saveDashboardPreferencesInternal(
    userId: string,
    preferences: Partial<DashboardPreferences>
  ): Promise<void> {
    try {
      const preferencesRef = doc(
        this.firestore,
        `users/${userId}/preferences/dashboard`
      )

      await updateDoc(preferencesRef, {
        ...preferences,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error saving dashboard preferences:", error)
      // Could implement retry logic here
    }
  }

  /**
   * Updates status filters for a user
   * @param userId The user's UID
   * @param filters Array of status filter values
   */
  updateStatusFilters(userId: string, filters: string[]): void {
    this.saveDashboardPreferences(userId, { statusFilters: filters })
  }

  /**
   * Updates chime enabled setting for a user
   * @param userId The user's UID
   * @param enabled Whether chime is enabled
   */
  updateChimeEnabled(userId: string, enabled: boolean): void {
    this.saveDashboardPreferences(userId, { chimeEnabled: enabled })
  }

  /**
   * Updates summary type filter for a user
   * @param userId The user's UID
   * @param filter Summary type filter value
   */
  updateSummaryTypeFilter(userId: string, filter: string): void {
    this.saveDashboardPreferences(userId, { summaryTypeFilter: filter })
  }

  /**
   * Updates owner edited filter for a user
   * Validates the filter value before saving
   * @param userId The user's UID
   * @param filter Owner edited filter value ('all' | 'edited' | 'not-edited')
   */
  updateOwnerEditedFilter(userId: string, filter: OwnerEditedFilterType): void {
    // Validate input before saving
    if (!VALID_OWNER_EDITED_FILTERS.includes(filter)) {
      console.error(
        `[UserPreferencesService] Invalid owner edited filter value: ${filter}`
      )
      return
    }
    this.saveDashboardPreferences(userId, { ownerEditedFilter: filter })
  }

  /**
   * Updates the "Deleted" chip state for a user (CAB-597)
   * @param userId The user's UID
   * @param showDeleted Whether deleted charts are unioned into the list
   */
  updateDeletedFilter(userId: string, showDeleted: boolean): void {
    this.saveDashboardPreferences(userId, { showDeleted })
  }

  /**
   * Clears the cache for a user (e.g., on sign out)
   * @param userId The user's UID
   */
  clearCache(userId: string): void {
    this.dashboardPreferencesCache.delete(userId)
  }

  /**
   * Clears all cached preferences
   */
  clearAllCache(): void {
    this.dashboardPreferencesCache.clear()
  }
}
